import React from 'react';
import { Message, Activity, RecentActivity } from '../types';
import { format } from 'date-fns';
import { PaperAirplaneIcon, ChatBubbleLeftIcon, GlobeAltIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useAuthStore } from '../store/auth.ts';
import axios from '../config.ts';

export const ChatPage: React.FC = () => {
    const [messages, setMessages] = React.useState<Message[]>([]);
    const [activities, setActivities] = React.useState<RecentActivity[]>([]);
    const [input, setInput] = React.useState('');
    const [isLoading, setIsLoading] = React.useState(false);
    const [isLoadingActivities, setIsLoadingActivities] = React.useState(false);
    const messagesEndRef = React.useRef<HTMLDivElement>(null);
    const inputRef = React.useRef<HTMLTextAreaElement>(null);
    const { user } = useAuthStore();

    const fetchRecentActivity = async () => {
        if (!user) return;
        
        setIsLoadingActivities(true);
        try {
            const response = await axios.post('/api/recent-activity', { 
                limit: 10 
            });
            
            if (response.data.success) {
                setActivities(response.data.activities);
            }
        } catch (error) {
            console.error('Error fetching recent activity:', error);
        } finally {
            setIsLoadingActivities(false);
        }
    };

    // Fetch recent activities and refresh every 10 minutes
    React.useEffect(() => {
        fetchRecentActivity();
        const interval = setInterval(fetchRecentActivity, 10 * 60 * 1000);
        return () => clearInterval(interval);
    }, [user]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    React.useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const { user } = useAuthStore.getState();
        if (!input.trim() || isLoading || !user) return;

        const userMessage: Message = {
            id: Date.now().toString(),
            content: input.trim(),
            isUser: true,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);

        try {
            const response = await axios.post('/api/ask', { 
                message: input.trim() 
            });

            const botMessage: Message = {
                id: (Date.now() + 1).toString(),
                content: response.data.response,
                isUser: false,
                timestamp: new Date()
            };

            setMessages(prev => [...prev, botMessage]);
        } catch (error) {
            console.error('Error sending message:', error);
            const errorMessage: Message = {
                id: (Date.now() + 1).toString(),
                content: 'Sorry, I encountered an error. Please try again.',
                isUser: false,
                timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    return (
        <div className="flex h-[calc(100vh-4rem)]">
            {/* Activity Sidebar */}
            <div className="w-80 bg-white border-r border-gray-200 overflow-y-auto">
                <div className="p-4">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
                    {isLoadingActivities ? (
                        <div className="space-y-3">
                            {[...Array(3)].map((_, i) => (
                                <div key={i} className="animate-pulse">
                                    <div className="h-16 bg-gray-100 rounded-lg"></div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {activities.map((activity) => (
                                <a
                                    key={activity.section_id}
                                    href={activity.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="text-gray-500 group-hover:text-primary-600">
                                            <GlobeAltIcon className="h-4 w-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate">
                                                {activity.title || 'Untitled'}
                                            </p>
                                            <p className="text-xs text-gray-500 truncate">
                                                {activity.url}
                                            </p>
                                        </div>
                                        <span className="text-xs text-gray-400 whitespace-nowrap">
                                            {format(new Date(activity.timestamp), 'HH:mm')}
                                        </span>
                                    </div>
                                    <p className="mt-1 text-xs text-gray-600 line-clamp-2">
                                        {activity.content_preview}
                                    </p>
                                </a>
                            ))}
                            {activities.length === 0 && (
                                <div className="text-center text-gray-500 py-4">
                                    No recent activity
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Chat Area */}
            <div className="flex-1 flex flex-col bg-gray-50">
                <div className="flex-1 overflow-y-auto p-4">
                    <div className="max-w-3xl mx-auto space-y-4">
                        {messages.map((message) => (
                            <div
                                key={message.id}
                                className={clsx(
                                    'flex w-full',
                                    message.isUser ? 'justify-end' : 'justify-start'
                                )}
                            >
                                <div
                                    className={clsx(
                                        'max-w-md rounded-lg p-4',
                                        message.isUser
                                            ? 'bg-primary-600 text-white'
                                            : 'bg-white text-gray-900'
                                    )}
                                >
                                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                                    <p
                                        className={clsx(
                                            'text-xs mt-1',
                                            message.isUser ? 'text-primary-100' : 'text-gray-400'
                                        )}
                                    >
                                        {format(message.timestamp, 'HH:mm')}
                                    </p>
                                </div>
                            </div>
                        ))}
                        <div ref={messagesEndRef} />
                    </div>
                </div>

                <div className="border-t border-gray-200 bg-white p-4">
                    <div className="max-w-3xl mx-auto">
                        <form onSubmit={handleSubmit} className="flex items-end gap-4">
                            <div className="flex-1">
                                <textarea
                                    ref={inputRef}
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Type your message..."
                                    className="w-full resize-none rounded-lg border-gray-300 focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                    rows={1}
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={isLoading || !input.trim()}
                                className={clsx(
                                    'rounded-lg p-2 text-white',
                                    isLoading || !input.trim()
                                        ? 'bg-gray-300 cursor-not-allowed'
                                        : 'bg-primary-600 hover:bg-primary-700'
                                )}
                            >
                                <PaperAirplaneIcon className="h-5 w-5" />
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};
